type WinWithEnv = Window & typeof globalThis & { env?: { VITE_ENV?: string } };

const isProd = (): boolean => {
  const env = (globalThis as WinWithEnv).env;
  return env?.VITE_ENV === 'prod-master' || env?.VITE_ENV === 'prod-eu';
};

const getSwaggerUrl = (): string =>
  isProd()
    ? 'reference'
    : typeof window !== 'undefined'
    ? `${window.location.protocol}//${window.location.hostname.replace(
        'console',
        'api'
      )}/spec/`
    : '';

// prettier-ignore
const DOCS_LINKS = {
  castai_kvisor_docs_enable_private_image_scan:
    'docs/kvisor#enable-private-image-scan-v0370',
  castai_kvisor_docs: 'docs/kvisor',
  castai_swagger: getSwaggerUrl(),
  docs: 'docs/',
  carpenter_docs: 'docs/migration-from-karpenter',
  docs_agent_upgrade: 'docs/helm-charts#eks',
  docs_overview: 'docs/getting-started',
  docs_spot: 'docs/spot',
  docs_sso: 'docs/sso',
  docs_sso_group_sync: 'docs/sso-user-group-sync',
  docs_disconnect: 'docs/disconnect-your-cluster',
  docs_remove_resource:
    'docs/disconnect-your-cluster#removing-cast-ai-credentials-and-other-resources',
  docs_evictor: 'docs/evictor',
  docs_evictor_aggressive_mode: 'docs/evictor#aggressive-mode',
  docs_evictor_advanced_configuration: 'docs/evictor#advanced-configuration',
  docs_assign_credentials_eks: 'docs/credential-onboarding#eks',
  docs_assign_credentials_gke: 'docs/credential-onboarding#gke',
  docs_gpu_config: 'docs/gpu#configuration',
  docs_gpu_metrics_exporter: 'docs/gpu-metrics-exporter',
  docs_unscheduled_pods: 'docs/autoscaler-checklist',
  docs_how_it_works: 'docs/how-it-works',
  docs_interruption_prediction_model: 'docs/spot#interruption-prediction-model',
  docs_autoscaler_scoped: 'docs/scoped-autoscaler',
  docs_node_configuration_required_iam_permissions:
    'docs/node-configuration#required-iam-permissions',
  docs_upgrading_agent: 'docs/troubleshooting#upgrading-the-agent',
  docs_private_image_scan:
    'docs/kvisor?_gl=1*1cvearm*_gcl_au*MjA2MTQxMTUxNS4xNjkzMzc2NDkx&_ga=2.137198708.51767698.1699854582-646632864.1638359834#enable-private-image-scan',
  docs_kvisor_config: 'docs/kvisor#configuring-features',
  docs_dbo: 'docs/dbo-introduction',
  docs_dbo_quick_start: 'docs/dbo-aws-rds-and-aurora-quick-start',
  docs_dbo_quick_start_manual_setup:
    'docs/dbo-getting-started#manual-any-postgresql-or-mysql',
  docs_dbo_how_does_it_work: 'docs/dbo-how-does-it-work',
  docs_dbo_cache_management: 'docs/dbo-caching',
  docs_dbo_pooling: 'docs/dbo-connection-pooling-overview',
  docs_dbo_performance_advisor: 'docs/dbo-performance-advisor',
  docs_dbo_permissions: 'docs/dbo-access-requirements-and-permissions',
  docs_dbo_permissions_gcp:
    'docs/dbo-access-requirements-and-permissions#automated-discovery-on-google-cloud',
  docs_dbo_permissions_aws:
    'docs/dbo-access-requirements-and-permissions#automated-discovery-on-aws',
  docs_woop: 'docs/workload-autoscaling-overview',
  docs_woop_upgrading: 'docs/workload-autoscaling-configuration#upgrading',
  docs_woop_confidence:
    'docs/woop-configuration-settings#recommendation-confidence',
  docs_woop_annotations:
    'docs/workload-autoscaling-configuration#configuration-via-annotations',
  docs_woop_hpa: 'docs/horizontal-pod-autoscaling',
  docs_woop_sensitivity:
    'docs/workload-autoscaling-configuration#change-sensitivity',
  docs_woop_single_replica:
    'docs/workload-autoscaling-configuration#single-replica-workload-management',
  docs_woop_predictive_scaling:
    'docs/workload-autoscaling-configuration#predictive-workload-scaling',
  docs_woop_in_place_resizing: 'docs/woop-in-place-resizing',
  docs_woop_vpa_selective_optimization:
    'docs/workload-autoscaling-configuration#resource-specific-optimization',
  docs_woop_vpa_stall_detection:
    'docs/woop-configuration-settings#stall-detection',
  docs_woop_vpa_memory_growth_detection: 'docs/workload-autoscaling-overview',
  docs_woop_vpa_apply_type:
    'docs/workload-autoscaling-configuration#recommendation-apply-type',
  docs_woop_vpa_percentile:
    'docs/workload-autoscaling-configuration#recommendation-percentile',
  docs_woop_vpa_overhead:
    'docs/workload-autoscaling-configuration#resource-overhead',
  docs_woop_vpa_resource_limits:
    'docs/workload-autoscaling-configuration#workload-resource-limits',
  docs_woop_vpa_constraints:
    'docs/workload-autoscaling-configuration#workload-autoscaler-constraints',
  docs_woop_vpa_lookback:
    'docs/workload-autoscaling-configuration#look-back-period',
  docs_woop_vpa_startup_metrics:
    'docs/workload-autoscaling-configuration#ignore-startup-metrics',
  docs_woop_hpa_v2: 'docs/workload-autoscaler-hpa-v2',
  docs_woop_hpa_v2_migration: 'docs/woop-hpa-how-to-migrate',
  docs_image_scanning: 'docs/image-scanning',
  docs_cluster_efficiency:
    'docs/cluster-efficiency-report#how-to-understand-metrics-in-this-report',
  docs_node_templates: 'docs/node-templates',
  docs_partial_node_template_matching:
    'docs/node-templates#node-template-matching',
  docs_webhook_examples: 'docs/examples',
  docs_kvisor_upgrade: 'docs/kvisor#upgrade-the-kvisor-agent',
  docs_runtime_security: 'docs/sec-runtime-security',
  docs_runtime_rule_resource_selector:
    'docs/anomaly-rules-engine#resource-selectors',
  docs_runtime_cel_rule_text:
    'docs/anomaly-rules-engine#user-defined-cel-rules',
  docs_runtime_custom_lists: 'docs/anomaly-rules-engine#custom-lists',
  docs_ai_optimizer: 'docs/getting-started-ai',
  docs_model_quantization_and_precision:
    'docs/getting-started-ai#model-quantization-and-precision',
  docs_model_supported_endpoints: 'docs/getting-started-ai#supported-endpoints',
  docs_org_cluster_cost_report: 'docs/organizational-cluster-cost-report',
  docs_rebalancing: 'docs/rebalancing',
  docs_scheduled_rebalancing: 'docs/scheduled-rebalancing',
  docs_rebalancing_handling_failed_node_drains:
    'docs/rebalancing#handling-failed-node-drains',
  docs_commitments: 'docs/commitments',
  docs_discount_engine: 'docs/discount-engine-overview#discount-engine',
  docs_network_cost: 'docs/network-cost',
  docs_network_observability: 'docs/network-observability',
  docs_gpu_utilization: 'docs/gpu-utilization',
  docs_autoscaler: 'docs/autoscaler',
  docs_workload_autoscaler: 'docs/workload-autoscaling-overview',
  docs_workload_autoscaler_failed_helm_test:
    'docs/workload-autoscaling-overview#failed-helm-test-hooks',
  docs_audit: 'docs/audit-log',
  docs_trial: 'docs/cast-ai-free-trial',
  docs_pod_mutations: 'docs/pod-mutations',
  docs_pod_mutations_enforcement:
    'docs/pod-mutations-overview#enabling-enforcement',
  docs_pod_pinner: 'docs/pod-pinner',
  docs_cluster_score: 'docs/cluster-score',
  docs_terraform_provider: 'docs/terraform-provider',
  docs_cluster_hibernate: 'docs/autoscaling-cluster-hibernation',
  docs_resume_cluster_node_config:
    'reference/externalclusterapi_triggerresumecluster',
  docs_hibernation_aws_requirements:
    'docs/autoscaling-cluster-hibernation#aws-requirements',
  docs_available_savings: 'docs/available-savings',
  docs_eks_maximum_pods_formula: 'docs/node-configuration#maximum-pods-formula',
  docs_gke_maximum_pods_formula:
    'docs/node-configuration#gke-max-pods-per-node-configuration',
  docs_cloud_connect_permissions:
    'docs/cloud-permissions#permissions-for-cloud-connect',
  docs_cloud_scan_permissions:
    'docs/cloud-permissions#permissions-for-cloud-scan',
  docs_cost_comparison_availability:
    'docs/cost-comparison#data-availability-requirements',
  docs_omni_overview: 'docs/omni-overview',
  docs_omni_custom_edge_location: 'docs/omni-custom-edge-location',
  docs_omni_getting_started: 'docs/omni-getting-started',
  docs_container_live_migration: 'docs/container-live-migration',
  docs_container_live_migration_getting_started: 'docs/clm-getting-started',
  docs_clm_overview: 'docs/clm-overview',
  docs_cast_operator: 'docs/castai-operator',
  docs_kubernetes_permissions: 'docs/kubernetes-permissions',
  docs_kubernetes_operator_permissions:
    'docs/kubernetes-permissions#the-cast-ai-operators-permissions',
  docs_kent: 'docs/karpenter-enterprise',
  docs_kent_kentroller: 'docs/karpenter-enterprise-kentroller',
  docs_apa_runbooks: 'docs/apa-runbooks',
  docs_slack_notifications:
    'docs/observability-tutorial-set-up-slack-notifications',
  docs_image_security: 'docs/image-security',
  docs_best_practices: 'docs/best-practices',
  docs_platform_usage: 'docs/administration-platform-usage',
  about_the_read_only_agent: 'docs/about-the-read-only-agent',
  docs_cast_mcp: 'docs/castpilot-mcp',
};

export type DocsLinkKey = keyof typeof DOCS_LINKS;

export const getDocsLink = (key: DocsLinkKey) => {
  const path = DOCS_LINKS[key];
  return /^https?:\/\//.test(path) ? path : `https://docs.cast.ai/${path}`;
};
